import React from 'react';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Button, { ILink } from 'tbk-components/src/components/Button';

export interface ICTABarProps extends IBaseElementProps {
	/**
	 * CTA Bar Heading
	 */
	heading?: string;
	/**
	 * CTA Bar Text
	 * @control textarea
	 */
	text?: string;
	/**
	 * CTA Bar Button
	 */
	button?: ILink;
	/**
	 * CTA Bar Inline Image, for use with Inline CTA - Image should be 224px X 50px
	 */
	inlineImage?: IImage;
	/**
	 * CTA Bar Background Image, for use with Default CTA and Main Page CTA - Image should be 1440px X 485px for Default CTA and 1440px X 380px for Main Page CTA
	 */
	backgroundImage?: IImage;
	/**
	 * CTA Bar Type Selection
	 */
	layout?: 'Default' | 'Inline' | 'Main Page';
}

/**
 * Theme Hero Banner
 * @block
 * @requiresHooks
 * @icon editor-insertmore
 */
const CTABar: React.FC<ICTABarProps> = ({
	heading,
	text,
	button,
	inlineImage,
	backgroundImage,
	layout = 'Default',
	className,
	classNames = [],
}) => {
	let content = null;
	switch (layout) {
		case 'Default':
			content = (
				<div className={cn(`cta-bar-${kebabCase(layout)}`)}>
					<div className="cta-bar__background-overlay"></div>
					<div
						className="cta-bar__background-image"
						style={
							backgroundImage
								? {
										backgroundImage: `url("${backgroundImage.src}")`,
								  }
								: {}
						}
					></div>
					<div className="container">
						<div className="cta-bar__content-container">
							<div className="cta-bar__content">
								{heading && (
									<h2 className="cta-bar__heading">
										{heading}
									</h2>
								)}
								{text && (
									<h4 className="cta-bar__text">{text}</h4>
								)}
							</div>
							{button && (
								<div className="cta-bar__button">
									<Button
										className="btn btn-lg btn-logo-blue"
										{...button}
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			);
			break;
		case 'Inline':
			content = (
				<div className={cn(`cta-bar-${kebabCase(layout)}`)}>
					<div className="container">
						<div className="cta-bar__content-container">
							<div className="cta-bar__inline-image">
								{inlineImage && (
									<Image
										{...inlineImage}
										className="img-fluid"
									/>
								)}
							</div>
							<div className="cta-bar__content">
								{heading && (
									<h2 className="cta-bar__heading">
										{heading}
									</h2>
								)}
								{text && (
									<div className="cta-bar__text">{text}</div>
								)}
							</div>
							{button && (
								<div className="cta-bar__button">
									<Button {...button} />
								</div>
							)}
						</div>
					</div>
				</div>
			);
			break;
		case 'Main Page':
			content = (
				<div className={cn(`cta-bar-${kebabCase(layout)}`)}>
					<div className="cta-bar__background-overlay">
						<div className="container">
							<div className="cta-bar__content-container">
								<div className="cta-bar__content">
									{heading && (
										<h2 className="cta-bar__heading">
											{heading}
										</h2>
									)}
									{text && (
										<h4 className="cta-bar__text">
											{text}
										</h4>
									)}
								</div>
								{button && (
									<div className="cta-bar__button">
										<Button
											className="btn btn-lg btn-logo-blue"
											{...button}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
					<div
						className="cta-bar__background-image"
						style={
							backgroundImage
								? {
										backgroundImage: `url("${backgroundImage.src}")`,
								  }
								: {}
						}
					></div>
				</div>
			);
			break;
	}

	return (
		<div className={className || cn('cta-bar', ...classNames)}>
			{content}
		</div>
	);
};

export default CTABar;
